import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PlusIcon, 
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import stockService from '../../services/stockService';

const StockEntryForm = () => {
  const navigate = useNavigate();
  const { 
    products: contextProducts,
    suppliers: contextSuppliers,
    stockItems,
    addStockMovement,
    updateProductStock,
    fetchAllData
  } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  
  const [entryData, setEntryData] = useState({
    entryNumber: '',
    date: new Date().toISOString().split('T')[0],
    supplierId: '',
    reference: '',
    documentType: 'bon-reception',
    location: '',
    notes: '',
    items: []
  });
  
  // Utiliser uniquement les données du contexte pour une interconnexion en temps réel
  const suppliers = contextSuppliers || [];
  
  // Utiliser les vrais entrepôts du système (warehouses)
  const { warehouses } = useAppData();
  const entrepots = warehouses && warehouses.length > 0 
    ? warehouses.map(warehouse => ({ id: warehouse.id, name: warehouse.name }))
    : [];
  
  // Utiliser uniquement les produits du contexte, pas de données fictives
  const products = contextProducts || [];

  // Assurer le chargement des données au montage du composant
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Générer un numéro d'entrée
  useEffect(() => {
    if (!entryData.entryNumber) {
      const entryNumber = `ENT-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      setEntryData(prev => ({ ...prev, entryNumber }));
    }
  }, [entryData.entryNumber]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEntryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredProducts = searchQuery.trim() === ''
    ? products.slice(0, 50) // Show the first 50 products when no search is performed
    : products.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()))
      );

  const handleAddItem = (product) => {
    const newItem = {
      id: product.id,
      name: product.name,
      sku: product.sku || '',
      unit: product.unit || 'Pièce',
      quantity: 1,
      unitPrice: product.price || 0,
      total: product.price || 0
    };
    
    // Check if product already exists in items
    const existingIndex = entryData.items.findIndex(item => item.id === product.id);
    if (existingIndex !== -1) {
      // If exists, increment quantity
      const updatedItems = [...entryData.items];
      updatedItems[existingIndex].quantity += 1;
      updatedItems[existingIndex].total = updatedItems[existingIndex].quantity * updatedItems[existingIndex].unitPrice;
      
      setEntryData(prev => ({
        ...prev,
        items: updatedItems
      }));
    } else {
      // If new, add to items
      setEntryData(prev => ({
        ...prev,
        items: [...prev.items, newItem]
      }));
    }
    
    setSearchQuery('');
    setShowProductSearch(false);
  };

  const handleRemoveItem = (index) => {
    setEntryData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...entryData.items];
    
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? parseFloat(value) : updatedItems[index].quantity;
      const unitPrice = field === 'unitPrice' ? parseFloat(value) : updatedItems[index].unitPrice;
      
      updatedItems[index][field] = parseFloat(value);
      updatedItems[index].total = quantity * unitPrice;
    } else {
      updatedItems[index][field] = value;
    }
    
    setEntryData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!entryData.supplierId) {
      toast.error('Veuillez sélectionner un fournisseur');
      return;
    }
    
    if (!entryData.location) {
      toast.error('Veuillez sélectionner un entrepôt');
      return;
    }
    
    if (entryData.items.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Créer le mouvement de stock
      const stockMovement = {
        id: entryData.entryNumber,
        date: entryData.date,
        type: 'entrée',
        supplierId: entryData.supplierId,
        reference: entryData.reference,
        documentType: entryData.documentType,
        location: entryData.location, // Keep original field for API compatibility
        warehouse: entryData.location, // Also include warehouse property for consistency
        quantity: entryData.items.reduce((total, item) => total + parseInt(item.quantity || 0), 0), // Add total quantity
        productId: entryData.items.length === 1 ? entryData.items[0].id : null, // Set productId if single product
        notes: entryData.notes,
        items: entryData.items,
        totalValue: calculateTotal()
      };
      
      // Process each item using StockService
      console.log('Using StockService for stock entry');
      const processedItems = [];
      const failedItems = [];
      
      for (const item of entryData.items) {
        try {
          // Update product stock using StockService
          await stockService.updateProductStock(item.id, {
            type: 'entrée',
            quantity: item.quantity,
            warehouse: entryData.location,
            reference: entryData.reference,
            notes: entryData.notes || `Entrée de stock: ${entryData.entryNumber}`
          });
          
          processedItems.push(item.id);
          console.log(`Successfully processed item ${item.id}`);
        } catch (itemError) {
          console.error(`Error processing item ${item.id}:`, itemError);
          failedItems.push(item.id);
        }
      }
      
      // Create the overall movement record if needed
      if (addStockMovement && processedItems.length > 0) {
        stockMovement.fromEntryForm = true;
        stockMovement.processedItems = processedItems;
        stockMovement.failedItems = failedItems;
        addStockMovement(stockMovement);
      }
      
      // Mettre à jour les données
      fetchAllData();
      
      toast.success('Entrée de stock enregistrée avec succès');
      setIsLoading(false);
      navigate('/stock');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'entrée de stock:', error);
      toast.error('Erreur lors de l\'enregistrement de l\'entrée de stock');
      setIsLoading(false);
    }
  };

  const calculateTotal = () => {
    // Make sure we're adding numbers, not concatenating strings
    return entryData.items.reduce((sum, item) => {
      return sum + (typeof item.total === 'number' ? item.total : Number(item.total));
    }, 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/stock')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Entrée de Stock
            </h1>
            <p className="text-white/60 mt-1">
              Enregistrez les nouvelles entrées de produits en stock
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations générales */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-6">Informations générales</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="entryNumber" className="block text-white font-medium mb-1">
                N° d'entrée
              </label>
              <input
                type="text"
                id="entryNumber"
                name="entryNumber"
                value={entryData.entryNumber}
                readOnly
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="date" className="block text-white font-medium mb-1">
                Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={entryData.date}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="supplierId" className="block text-white font-medium mb-1">
                Fournisseur <span className="text-red-400">*</span>
              </label>
              <select
                id="supplierId"
                name="supplierId"
                value={entryData.supplierId}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">-- Sélectionner un fournisseur --</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="location" className="block text-white font-medium mb-1">
                Entrepôt <span className="text-red-400">*</span>
              </label>
              <select
                id="location"
                name="location"
                value={entryData.location}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">-- Sélectionner un entrepôt --</option>
                {entrepots.map(entrepot => (
                  <option key={entrepot.id} value={entrepot.id}>{entrepot.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Document de référence */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-6">Document de référence</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="documentType" className="block text-white font-medium mb-1">
                Type de document
              </label>
              <select
                id="documentType"
                name="documentType"
                value={entryData.documentType}
                onChange={handleInputChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="bon-reception">Bon de Réception</option>
                <option value="bon-livraison">Bon de Livraison</option>
                <option value="facture">Facture</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label htmlFor="reference" className="block text-white font-medium mb-1">
                N° de référence
              </label>
              <input
                type="text"
                id="reference"
                name="reference"
                value={entryData.reference}
                onChange={handleInputChange}
                placeholder="Ex: BR-2023-001"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Produits */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-white">Produits</h3>
            <button
              type="button"
              onClick={() => setShowProductSearch(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Ajouter un produit</span>
            </button>
          </div>

          {/* Search Product Panel */}
          {showProductSearch && (
            <div className="mb-6 p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center mb-4">
                <h4 className="text-white font-medium">Rechercher un produit</h4>
                <button 
                  type="button"
                  onClick={() => setShowProductSearch(false)}
                  className="ml-auto text-white/60 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou référence..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-3 pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Nom</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Référence</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Unité</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-white/70">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map(product => (
                        <tr key={product.id} className="hover:bg-white/10 transition-colors">
                          <td className="px-4 py-2 text-white">{product.name || 'Produit sans nom'}</td>
                          <td className="px-4 py-2 text-white/70">{product.sku || '-'}</td>
                          <td className="px-4 py-2 text-white/70">{product.unit || '-'}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleAddItem(product)}
                              className="px-3 py-1 bg-blue-600/40 text-blue-200 rounded-lg hover:bg-blue-600/60 transition-colors text-xs font-medium"
                            >
                              Ajouter
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : searchQuery.trim() !== '' ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-4 text-center text-white/60">
                          Aucun produit trouvé
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-4 py-4 text-center text-white/60">
                          Chargement des produits...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Items Table */}
          {entryData.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Produit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Référence</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Qté</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Prix unitaire</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {entryData.items.map((item, index) => (
                    <tr key={item.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-white">{item.name}</td>
                      <td className="px-4 py-3 text-white/70">{item.sku}</td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', Math.max(1, parseInt(e.target.value)))}
                          min="1"
                          className="w-20 p-1 bg-white/10 border border-white/20 rounded-lg text-white text-right"
                        />
                        <span className="ml-1 text-white/60">{item.unit}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', Math.max(0, parseFloat(e.target.value)))}
                          min="0"
                          step="0.01"
                          className="w-24 p-1 bg-white/10 border border-white/20 rounded-lg text-white text-right"
                        />
                        <span className="ml-1 text-white/60">DA</span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-white">{item.total.toLocaleString()} DA</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-white/5">
                    <td colSpan="4" className="px-4 py-3 text-right font-medium text-white">Total</td>
                    <td className="px-4 py-3 text-right font-medium text-white">
                      {/* Use Number parsing to ensure we're working with numbers not strings */}
                      {Number(calculateTotal()).toLocaleString()} DA
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
              <p className="text-white/50">Aucun produit ajouté</p>
              <button
                type="button"
                onClick={() => setShowProductSearch(true)}
                className="mt-2 px-4 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                Ajouter des produits
              </button>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">Notes</h3>
          <textarea
            name="notes"
            id="notes"
            value={entryData.notes}
            onChange={handleInputChange}
            rows="3"
            className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Notes ou commentaires sur cette entrée de stock..."
          ></textarea>
        </div>

        {/* Boutons de soumission */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/stock')}
            className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 flex items-center space-x-2 disabled:opacity-70"
          >
            {isLoading ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                <span>Enregistrement...</span>
              </>
            ) : (
              <span>Enregistrer</span>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default StockEntryForm; 